import { z } from 'zod';

const envSchema = z.object({
  NODE_ENV: z.enum(['development', 'test', 'qa', 'staging', 'production']),

  // Jusfy Backend API
  REACT_APP_API_URL: z.url(),

  // auth-service. Sem valor, só a sessão legada é usada.
  // Origem, sem path: https://auth.jusfy.dev
  REACT_APP_AUTH_SERVICE_URL: z
    .union([z.url(), z.literal('')])
    .default('')
    .optional(),

  // Jusfy Frontend App
  REACT_APP_APP_URL: z.url().optional(),

  // JusPDF
  REACT_APP_PDF_URL: z.url().optional(),
  REACT_APP_PDF_DOWNLOAD_BASE_URL: z.url().optional(),

  // Report Service
  REACT_APP_REPORT_SERVICE_URL: z.url().optional(),

  // JusGPT
  REACT_APP_JUSGPT_API: z.url().optional(),

  // Jusbill
  REACT_APP_JUSBILL_URL: z.url().optional(),

  // JusfyPay
  REACT_APP_JUSFYPAY_URL: z.url().default('https://pay.jusfy.com.br'),

  // Antecipação de processos (jusfy-lawsuit-finance-front, embed no menu Jusfy Pay)
  REACT_APP_LAWSUIT_FINANCE_URL: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.url().default('https://jusfy-lawsuit-finance-front.jusfy.com.br'),
  ),

  // JusClass (microfrontend embed)
  REACT_APP_JUSCLASS_WEB_URL: z
    .url()
    .default('https://jusclass-web-dev.jusfy.dev'),

  // Jurisprudência (microfrontend embed)
  REACT_APP_JURISPRUDENCIA_WEB_URL: z
    .url()
    .default('https://jusfy-jurisprudencia.jusfy.com.br'),

  // LP Manager (microfrontend embed)
  REACT_APP_LP_MANAGER_URL: z
    .url()
    .default('https://jusfy-lp-manager.jusfy.com.br'),

  // JusRecruta (microfrontend embed)
  REACT_APP_JUSRECRUTA_URL: z.url().default('https://jusrecruta.jusfy.com.br'),
  // API pública do jusrecruta-backend (GET /academy/oferta: modo free|freemium|paid).
  REACT_APP_JUSRECRUTA_API_URL: z.url().default('https://jusrecruta.jusfy.com.br/v1'),

  // Plano academy/JusRecruta: ids dos planos (jussub) que caracterizam o
  // aluno. Lista separada por virgula; vazia = ninguem e aluno.
  REACT_APP_ACADEMY_PLAN_IDS: z.string().default(''),

  // Atalho de DESENVOLVIMENTO: user_ids tratados como aluno independente do
  // plano. Ignorado em producao (ver helpers/academyPlan.ts).
  REACT_APP_ACADEMY_FORCE_USER_IDS: z.string().default(''),

  // Programa de Afiliados (landing page)
  REACT_APP_AFFILIATE_LANDING_URL: z
    .url()
    .default('https://page.jusfy.com.br/afiliados/'),

  // Recaptcha
  REACT_APP_RECAPTCHA_SITEKEY: z.string(),
  REACT_APP_RECAPTCHA_V2_SITEKEY: z.string(),
  REACT_APP_RECAPTCHA_V3_SITEKEY: z.string(),

  // Pagar.me
  REACT_APP_PAGARME_ENCRYPTION_KEY: z.string(),
  REACT_APP_PAGARME_V5_ENCRYPTION_KEY: z.string(),

  // CEP API
  REACT_APP_CEP_API_BASE_URL: z.url(),

  // Feature Flags
  REACT_APP_FEATURE_FLAG_API_URL: z.url(),
  REACT_APP_FEATURE_FLAG_CLIENT_KEY: z.string(),
  REACT_APP_FEATURE_FLAG_APP_NAME: z.string(),
  REACT_APP_FEATURE_FLAG_REFRESH_INTERVAL: z.coerce
    .number()
    .int()
    .nonnegative()
    .default(15),
  REACT_APP_JUSFY_FEATURE_FLAG_API_URL: z.url(),
  REACT_APP_FEATURE_FLAG_API_KEY: z.string(),

  // Sockets
  REACT_APP_SOCKET_URL: z.url(),
  REACT_APP_SOCKET_PATH: z.string(),

  // Sentry
  REACT_APP_SENTRY_ENV: z.string(),
  REACT_APP_SENTRY_SAMPLE_RATE: z.coerce
    .number()
    .min(0)
    .max(1)
    .default(0),

  REACT_APP_SENTRY_SAMPLE_TRACE_RATE: z.coerce
    .number()
    .min(0)
    .max(1)
    .default(0),

  // JusSign
  REACT_APP_JUSSIGN_MAX_EXPIRATION_DAYS: z.coerce
    .number()
    .int()
    .positive()
    .optional()
    .default(365),

  // Plugins
  DISABLE_ESLINT_PLUGIN: z.coerce.boolean().default(false),

  NOTIFY_TECH_CHANNEL: z
    .union([z.url(), z.literal('')])
    .optional()
    .default(''),

  REACT_APP_MAX_FILE_SIZE_MB: z.coerce
    .number()
    .int()
    .positive()
    .default(80),

  // Sidebar
  REACT_APP_SIDEBAR_NEW_TAG_DURATION_DAYS: z.coerce
    .number()
    .int()
    .positive()
    .default(30),

  // Customer Management Service
  REACT_APP_CUSTOMER_SERVICE_URL: z
    .url()
    .default('')
    .optional(),
  REACT_APP_CUSTOMER_API_URL: z
    .url()
    .default('')
    .optional(),
  // Central de ajuda das telas de Clientes — abre em nova aba pelo botão "Ajuda"
  // do PageTopBar. Vazia esconde nada: o link simplesmente não leva a lugar.
  REACT_APP_HELP_JUSFY_CUSTOMER_URL: z
    .url()
    .default('')
    .optional(),

  // Jusfy Drive (microfrontend embed)
  REACT_APP_DRIVE_URL: z
    .url()
    .default('')
    .optional(),
  // API do jusfy-drive-service, chamada DIRETO do browser (mesmo Bearer que
  // já vai pro customer-management-service) — hoje só pra
  // GET /files/essentials/counts na listagem de clientes.
  REACT_APP_DRIVE_API_URL: z
    .url()
    .default('')
    .optional(),

  // Plataforma de IA (aba nova, via handoff SSO). Vazia desliga o desvio do
  // item "IA da Jusfy" — quem está na allowlist volta pro JusGPT sem deploy.
  REACT_APP_AI_PLATFORM_URL: z
    .union([z.url(), z.literal('')])
    .default('')
    .optional(),

  // Gamification invite / referral
  REACT_APP_GAMIFICATION_INVITE_URL: z.string().optional().default(''),
  // Botão "Falar com suporte" (WhatsApp)
  REACT_APP_SUPPORT_WHATSAPP_URL: z.preprocess(
    (val) => (val === '' ? undefined : val),
    z.url().default(
      'https://api.whatsapp.com/send/?phone=5511936235959&text=&hsCtaAttrib=220935691828',
    ),
  ),
  // jushelp-whatsapp: recebe o formulário de contratação de correspondente.
  // Sem valor, a contratação de correspondente fica desabilitada.
  REACT_APP_JUSHELP_API_URL: z
    .union([z.url(), z.literal('')])
    .default('')
    .optional(),

  // Auth 2.0 Centralizado
  REACT_APP_AUTH_FRONT_URL: z
    .union([z.url(), z.literal('')])
    .default('')
    .optional(),
  REACT_APP_HYDRA_CLIENT_ID: z.string().default('').optional(),
  REACT_APP_HYDRA_AUDIENCE: z.string().default('jusfy').optional(),
  REACT_APP_HYDRA_SCOPE: z.string().default('jusfy:app:*').optional(),
}).refine(
  (data) => !data.REACT_APP_AUTH_FRONT_URL || !!data.REACT_APP_HYDRA_CLIENT_ID,
  {
    message: 'REACT_APP_HYDRA_CLIENT_ID é obrigatório quando REACT_APP_AUTH_FRONT_URL está definido',
    path: ['REACT_APP_HYDRA_CLIENT_ID'],
  },
);

const _env = envSchema.safeParse(process.env);

if (!_env.success) {
  console.error('❌ Invalid environment variables:');
  _env.error.issues.forEach((issue) => {
    console.error(`- ${issue.path.join('.')}: ${issue.message}`);
  });
  throw new Error('Invalid environment variables!');
}

const validatedEnv = _env.data;

export const config = deepFreeze({
  nodeEnv: validatedEnv.NODE_ENV,

  jusfyBackend: {
    apiUrl: validatedEnv.REACT_APP_API_URL,
  },

  authService: {
    url: validatedEnv.REACT_APP_AUTH_SERVICE_URL,
  },

  app: {
    url: validatedEnv.REACT_APP_APP_URL,
  },

  jusPdf: {
    url: validatedEnv.REACT_APP_PDF_URL,
    downloadBaseUrl: validatedEnv.REACT_APP_PDF_DOWNLOAD_BASE_URL,
  },

  reportService: {
    url: validatedEnv.REACT_APP_REPORT_SERVICE_URL,
  },

  jusGpt: {
    apiUrl: validatedEnv.REACT_APP_JUSGPT_API,
  },

  jusbill: {
    url: validatedEnv.REACT_APP_JUSBILL_URL,
  },

  jusfyPay: {
    url: validatedEnv.REACT_APP_JUSFYPAY_URL,
  },

  lawsuitFinance: {
    url: validatedEnv.REACT_APP_LAWSUIT_FINANCE_URL,
  },

  affiliates: {
    landingUrl: validatedEnv.REACT_APP_AFFILIATE_LANDING_URL,
  },

  jusClass: {
    webUrl: validatedEnv.REACT_APP_JUSCLASS_WEB_URL,
  },

  jushelp: {
    apiUrl: validatedEnv.REACT_APP_JUSHELP_API_URL,
  },

  jurisprudencia: {
    webUrl: validatedEnv.REACT_APP_JURISPRUDENCIA_WEB_URL,
  },

  lpManager: {
    url: validatedEnv.REACT_APP_LP_MANAGER_URL,
  },

  jusrecruta: {
    url: validatedEnv.REACT_APP_JUSRECRUTA_URL,
    apiUrl: validatedEnv.REACT_APP_JUSRECRUTA_API_URL,
  },

  academy: {
    planIds: validatedEnv.REACT_APP_ACADEMY_PLAN_IDS,
    forceUserIds: validatedEnv.REACT_APP_ACADEMY_FORCE_USER_IDS,
  },

  recaptcha: {
    siteKey: validatedEnv.REACT_APP_RECAPTCHA_SITEKEY,
    siteKeyV2: validatedEnv.REACT_APP_RECAPTCHA_V2_SITEKEY,
    siteKeyV3: validatedEnv.REACT_APP_RECAPTCHA_V3_SITEKEY,
  },

  pagarme: {
    encryptionKey: validatedEnv.REACT_APP_PAGARME_ENCRYPTION_KEY,
    v5EncryptionKey: validatedEnv.REACT_APP_PAGARME_V5_ENCRYPTION_KEY,
  },

  cepApi: {
    baseUrl: validatedEnv.REACT_APP_CEP_API_BASE_URL,
  },

  featureFlag: {
    apiUrl: validatedEnv.REACT_APP_FEATURE_FLAG_API_URL,
    clientKey: validatedEnv.REACT_APP_FEATURE_FLAG_CLIENT_KEY,
    appName: validatedEnv.REACT_APP_FEATURE_FLAG_APP_NAME,
    refreshInterval: validatedEnv.REACT_APP_FEATURE_FLAG_REFRESH_INTERVAL,
    featureFlagApiUrl: validatedEnv.REACT_APP_JUSFY_FEATURE_FLAG_API_URL,
    featureFlagApiKey: validatedEnv.REACT_APP_FEATURE_FLAG_API_KEY,
  },

  sockets: {
    url: validatedEnv.REACT_APP_SOCKET_URL,
    path: validatedEnv.REACT_APP_SOCKET_PATH,
  },

  sentry: {
    env: validatedEnv.REACT_APP_SENTRY_ENV,
    sampleRate: validatedEnv.REACT_APP_SENTRY_SAMPLE_RATE,
  },

  jussign: {
    maxExpirationDays: validatedEnv.REACT_APP_JUSSIGN_MAX_EXPIRATION_DAYS,
    maxFileSizeMb: validatedEnv.REACT_APP_MAX_FILE_SIZE_MB,
    maxFileSizeBytes: validatedEnv.REACT_APP_MAX_FILE_SIZE_MB * 1024 * 1024,
  },

  plugins: {
    disableEslint: validatedEnv.DISABLE_ESLINT_PLUGIN,
  },

  slack: {
    notifyTechChannel: validatedEnv.NOTIFY_TECH_CHANNEL,
  },

  sidebar: {
    newTagDurationDays: validatedEnv.REACT_APP_SIDEBAR_NEW_TAG_DURATION_DAYS,
  },

  customerManagement: {
    url: validatedEnv.REACT_APP_CUSTOMER_SERVICE_URL,
    apiUrl: validatedEnv.REACT_APP_CUSTOMER_API_URL,
    helpUrl: validatedEnv.REACT_APP_HELP_JUSFY_CUSTOMER_URL,
  },

  drive: {
    url: validatedEnv.REACT_APP_DRIVE_URL,
    apiUrl: validatedEnv.REACT_APP_DRIVE_API_URL,
  },

  aiPlatform: {
    url: validatedEnv.REACT_APP_AI_PLATFORM_URL,
  },

  gamification: {
    inviteUrl: validatedEnv.REACT_APP_GAMIFICATION_INVITE_URL,
  },

  support: {
    whatsappUrl: validatedEnv.REACT_APP_SUPPORT_WHATSAPP_URL,
  },

  authFront: {
    url: validatedEnv.REACT_APP_AUTH_FRONT_URL,
  },

  hydra: {
    clientId: validatedEnv.REACT_APP_HYDRA_CLIENT_ID,
    audience: validatedEnv.REACT_APP_HYDRA_AUDIENCE,
    scope: validatedEnv.REACT_APP_HYDRA_SCOPE,
  },
});

function deepFreeze(obj) {
  if (!obj || typeof obj !== 'object' || Object.isFrozen(obj)) return obj;
  Object.getOwnPropertyNames(obj).forEach((prop) => {
    const value = obj[prop];
    if (value && typeof value === 'object') deepFreeze(value);
  });
  return Object.freeze(obj);
}
